// ===== Safety & Training Module Types =====

// ----- Type Unions -----

export type TopicStyle = 'Video' | 'PDF' | 'Poster' | 'FatalGram' | 'Other' | 'Uncategorized';

export type PlanStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED';

export type GoverningStandard = 'MSHA' | 'OSHA' | 'COMPANY';

export type ComplianceStatus = 'on-track' | 'behind' | 'at-risk';

// ----- Constants -----

export const TOPIC_STYLE_COLORS: Record<TopicStyle, string> = {
    Video: '#3b82f6',
    PDF: '#ef4444',
    Poster: '#8b5cf6',
    FatalGram: '#f59e0b',
    Other: '#6b7280',
    Uncategorized: '#9ca3af',
};

export const DEFAULT_WORKING_DAYS: number[] = [1, 2, 3, 4, 5]; // Monday through Friday (0=Sun, 6=Sat)

// ----- GraphQL Model Interfaces -----

export interface TrainingTopic {
    id: string;
    companyId: string;
    topicId: string;
    style: TopicStyle;
    description: string;
    timeValue: number;
    contentFilePath?: string | null;
    contentUrl?: string | null;
    categoryIds?: string[] | null;
    planIds?: string[] | null;
    companyResourceId?: string | null;
    sourceLibraryTopicId?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingPlan {
    id: string;
    companyId: string;
    name: string;
    governingStandard: GoverningStandard;
    minimumTimeRequired: number;
    cycleDuration: number;
    status: PlanStatus;
    categories: string; // AWSJSON - JSON.stringify(PlanCategory[])
    workingDays?: number[] | null;
    startDate?: string | null;
    holidays?: string | null; // AWSJSON - JSON.stringify(HolidayEntry[]) for custom holidays
    sourceTemplateId?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingSchedule {
    id: string;
    companyId: string;
    planId: string;
    topicId: string;
    scheduledDate: string;
    dayNumber: number;
    categoryId?: string | null;
    locked?: boolean | null;
    assignedUserId?: string | null; // null = everyone, string = specific user
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingEnrollment {
    id: string;
    companyId: string;
    userId: string;
    planId: string;
    enrolledDate: string;
    status: string;
    nextDueDate?: string | null;
    lastCompletionDate?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingCompletion {
    id: string;
    companyId: string;
    userId: string;
    topicId: string;
    planId: string;
    scheduleDate: string;
    completionDate: string;
    launched: boolean;
    markedAsViewed: boolean;
    timeValueMinutes: number;
    categoryId?: string | null;
    signaturePath?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingAffidavit {
    id: string;
    companyId: string;
    userId: string;
    adminId: string;
    planId: string;
    affidavitDate: string;
    signaturePath: string;
    legalText: string;
    nextDueDate: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface TrainingQuizResponse {
    id: string;
    companyId: string;
    userId: string;
    planId: string;
    categoryId: string;
    formId: string;
    responseData?: string; // AWSJSON
    signaturePath?: string;
    passed: boolean;
    score?: number;
    totalQuestions?: number;
    attemptNumber: number;
    completedAt: string;
    cycleStartDate: string;
    createdAt?: string;
    updatedAt?: string;
}

// ----- Plan Category (stored as AWSJSON on TrainingPlan.categories) -----

export interface PlanCategory {
    id: string;
    name: string;
    targetMinutes: number;
    requiresQuiz?: boolean;
    quizFormId?: string;
}

// ----- Computation / Input Interfaces -----

export interface ComplianceInput {
    totalMinutesViewed: number;
    totalMinutesRequired: number;
    daysElapsed: number;
    totalCycleDays: number;
}

export interface CategoryProgress {
    categoryId: string;
    categoryName: string;
    targetMinutes: number;
    completedMinutes: number;
    percentage: number;
}

export interface PlanValidation {
    valid: boolean;
    errors: string[];
}

export interface ImportResult {
    imported: TrainingTopicInput[];
    skipped: { topicId: string; reason: string }[];
}

export interface TrainingTopicInput {
    topicId: string;
    style: TopicStyle;
    description: string;
    timeValue: number;
    categoryIds?: string[];
}

// ----- View Switching -----

export type SafetyTrainingView =
    | { type: 'admin-dashboard' }
    | { type: 'my-training' }
    | { type: 'topics'; filterPlanId?: string }
    | { type: 'plan-builder'; planId?: string }
    | { type: 'schedule-builder'; planId?: string }
    | { type: 'user-profile'; userId: string }
    | { type: 'enrollment'; planId?: string }
    | { type: 'affidavit'; userId?: string }
    | { type: 'save-to-library' }
    | { type: 'import-from-library' };

// ----- Context Type -----

export interface SafetyTrainingContextType {
    // Data
    topics: TrainingTopic[];
    plans: TrainingPlan[];
    schedules: TrainingSchedule[];
    enrollments: TrainingEnrollment[];
    completions: TrainingCompletion[];
    quizResponses: TrainingQuizResponse[];
    isLoading: boolean;

    // Selected state
    selectedPlanId: string | null;
    setSelectedPlanId: (id: string | null) => void;

    // Computed
    getActivePlan: () => TrainingPlan | null;
    getTodaySchedule: (planId: string) => TrainingSchedule | null;
    getUserCompletions: (userId: string, planId: string) => TrainingCompletion[];
    getUserComplianceStatus: (userId: string, planId: string) => ComplianceStatus;
    getCategoryProgress: (userId: string, planId: string) => CategoryProgress[];
    getEnrolledUsers: (planId: string) => TrainingEnrollment[];

    // Mutations
    refreshData: () => Promise<void>;
    createTopic: (input: CreateTrainingTopicInput) => Promise<TrainingTopic>;
    updateTopic: (input: UpdateTrainingTopicInput) => Promise<TrainingTopic>;
    deleteTopic: (id: string) => Promise<void>;
    createPlan: (input: CreateTrainingPlanInput) => Promise<TrainingPlan>;
    updatePlan: (input: UpdateTrainingPlanInput) => Promise<TrainingPlan>;
    deletePlan: (id: string) => Promise<void>;
    createScheduleEntry: (input: CreateTrainingScheduleInput) => Promise<TrainingSchedule>;
    deleteScheduleEntry: (id: string) => Promise<void>;
    enrollUser: (userId: string, planId: string) => Promise<TrainingEnrollment>;
    unenrollUser: (enrollmentId: string) => Promise<void>;
    recordCompletion: (input: CreateTrainingCompletionInput) => Promise<TrainingCompletion>;
    createAffidavit: (input: CreateTrainingAffidavitInput) => Promise<TrainingAffidavit>;
}

// ----- GraphQL Input Types (for context mutations) -----

export interface CreateTrainingTopicInput {
    companyId: string;
    topicId: string;
    style: string;
    description: string;
    timeValue: number;
    contentFilePath?: string | null;
    contentUrl?: string | null;
    categoryIds?: string[] | null;
    planIds?: string[] | null;
}

export interface UpdateTrainingTopicInput {
    id: string;
    topicId?: string;
    style?: string;
    description?: string;
    timeValue?: number;
    contentFilePath?: string | null;
    contentUrl?: string | null;
    categoryIds?: string[] | null;
    planIds?: string[] | null;
}

export interface CreateTrainingPlanInput {
    companyId: string;
    name: string;
    governingStandard: string;
    minimumTimeRequired: number;
    cycleDuration: number;
    status: string;
    categories: string; // AWSJSON
    workingDays?: number[] | null;
    startDate?: string | null;
    holidays?: string | null;
}

export interface UpdateTrainingPlanInput {
    id: string;
    name?: string;
    governingStandard?: string;
    minimumTimeRequired?: number;
    cycleDuration?: number;
    status?: string;
    categories?: string;
    workingDays?: number[] | null;
    startDate?: string | null;
    holidays?: string | null;
}

export interface CreateTrainingScheduleInput {
    companyId: string;
    planId: string;
    topicId: string;
    scheduledDate: string;
    dayNumber: number;
    categoryId?: string | null;
    locked?: boolean | null;
    assignedUserId?: string | null;
}

export interface CreateTrainingCompletionInput {
    companyId: string;
    userId: string;
    topicId: string;
    planId: string;
    scheduleDate: string;
    completionDate: string;
    launched: boolean;
    markedAsViewed: boolean;
    timeValueMinutes: number;
    categoryId?: string | null;
    signaturePath?: string | null;
}

export interface CreateTrainingAffidavitInput {
    companyId: string;
    userId: string;
    adminId: string;
    planId: string;
    affidavitDate: string;
    signaturePath: string;
    legalText: string;
    nextDueDate: string;
}

// ----- Offline Queue Payload Types -----

export interface TrainingCompletionPayload {
    payloadType: 'TrainingCompletion';
    companyId: string;
    userId: string;
    topicId: string;
    planId: string;
    scheduleDate: string;
    launched: boolean;
    markedAsViewed: boolean;
    timeValueMinutes: number;
    categoryId: string | null;
    queuedAt: string;
}

export interface AffidavitPayload {
    payloadType: 'Affidavit';
    companyId: string;
    userId: string;
    adminId: string;
    planId: string;
    affidavitDate: string;
    legalText: string;
    signatureIndexedDBKey: string;
    nextDueDate: string;
    queuedAt: string;
}

export type QueuedTrainingPayload = TrainingCompletionPayload | AffidavitPayload;
